<script setup>
import { computed, ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { formatNumber } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';

import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter.vue';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  pageTitle: { type: String, required: true },
  hasAppliedFilters: { type: Boolean, required: true },
  hasActiveFolders: { type: Boolean, required: true },
  activeStatus: { type: String, required: true },
  isOnExpandedLayout: { type: Boolean, required: true },
  conversationStats: { type: Object, required: true },
  isListLoading: { type: Boolean, required: true },
  searchQuery: { type: String, default: '' },
  searchSince: { type: String, default: '' },
  searchUntil: { type: String, default: '' },
  allConversationsSelected: { type: Boolean, default: false },
  channelPhoneNumber: { type: String, default: '' },
  channelHealthStatus: { type: String, default: '' },
});

const emit = defineEmits([
  'addFolders',
  'deleteFolders',
  'resetFilters',
  'basicFilterChange',
  'filtersModal',
  'update:searchQuery',
  'update:searchSince',
  'update:searchUntil',
  'selectAllConversations',
]);

const isSearchOpen = ref(false);
const hasSearchFilters = computed(
  () => props.searchQuery || props.searchSince || props.searchUntil
);

const toggleSearch = () => {
  isSearchOpen.value = !isSearchOpen.value;
  if (!isSearchOpen.value) clearSearch();
};

const clearSearch = () => {
  emit('update:searchQuery', '');
  emit('update:searchSince', '');
  emit('update:searchUntil', '');
};

const { uiSettings, updateUISettings } = useUISettings();

const onBasicFilterChange = (value, type) => {
  emit('basicFilterChange', value, type);
};

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return props.hasAppliedFilters || props.hasActiveFolders;
});

const allCount = computed(() => props.conversationStats?.allCount || 0);
const formattedAllCount = computed(() => formatNumber(allCount.value));

const toggleConversationLayout = () => {
  const { LAYOUT_TYPES } = wootConstants;
  const {
    conversation_display_type: conversationDisplayType = LAYOUT_TYPES.CONDENSED,
  } = uiSettings.value;
  const newViewType =
    conversationDisplayType === LAYOUT_TYPES.CONDENSED
      ? LAYOUT_TYPES.EXPANDED
      : LAYOUT_TYPES.CONDENSED;
  updateUISettings({
    conversation_display_type: newViewType,
    previously_used_conversation_display_type: newViewType,
  });
};
</script>

<template>
  <div
    class="flex flex-col"
    :class="{
      'border-b border-n-strong': hasAppliedFiltersOrActiveFolders,
    }"
  >
    <div
      class="flex items-center justify-between gap-2 px-3"
      :class="channelHealthStatus ? 'min-h-[4rem] py-2' : 'h-[3.25rem]'"
    >
      <div class="flex flex-col justify-center min-w-0">
        <div class="flex items-center min-w-0">
          <h1
            class="text-base font-medium truncate text-n-slate-12"
            :title="pageTitle"
          >
            {{ pageTitle }}
          </h1>
          <span
            v-if="channelHealthStatus"
            class="inline-flex items-center gap-1 px-1.5 py-0.5 mx-1.5 rounded-md text-xxs font-medium shrink-0"
            :class="
              channelHealthStatus === 'healthy'
                ? 'bg-n-teal-3 text-n-teal-11'
                : 'bg-n-ruby-3 text-n-ruby-11'
            "
          >
            <span
              class="size-1.5 rounded-full"
              :class="
                channelHealthStatus === 'healthy'
                  ? 'bg-n-teal-9'
                  : 'bg-n-ruby-9'
              "
            />
            {{
              $t(
                `CHAT_LIST.CHANNEL_HEALTH.${channelHealthStatus === 'healthy' ? 'HEALTHY' : 'CRITICAL'}`
              )
            }}
          </span>
        </div>
        <span
          v-if="channelPhoneNumber"
          class="text-xs text-n-slate-10 truncate"
        >
          {{ channelPhoneNumber }}
        </span>
        <div class="flex items-center">
          <span
            v-if="
              allCount > 0 && hasAppliedFiltersOrActiveFolders && !isListLoading
            "
            class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
            :title="allCount"
          >
            {{ formattedAllCount }}
          </span>
          <span
            v-if="!hasAppliedFiltersOrActiveFolders"
            class="px-2 py-1 my-0.5 mx-1 rounded-md capitalize bg-n-slate-3 text-xxs text-n-slate-12 shrink-0"
          >
            {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${activeStatus}.TEXT`) }}
          </span>
        </div>
      </div>
      <div class="flex items-center gap-1">
        <NextButton
          v-tooltip.right="$t('CHAT_LIST.SEARCH.TOOLTIP')"
          icon="i-lucide-search"
          slate
          xs
          faded
          :class="{ '!bg-n-slate-3': isSearchOpen }"
          @click="toggleSearch"
        />
        <NextButton
          v-tooltip.right="$t('CHAT_LIST.BULK_SELECT.TOOLTIP')"
          icon="i-lucide-list-checks"
          slate
          xs
          faded
          :class="{ '!bg-n-slate-3': allConversationsSelected }"
          @click="emit('selectAllConversations', !allConversationsSelected)"
        />
        <template v-if="hasAppliedFilters && !hasActiveFolders">
          <div class="relative">
            <NextButton
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
              icon="i-lucide-save"
              slate
              xs
              faded
              @click="emit('addFolders')"
            />
            <div
              id="saveFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            icon="i-lucide-circle-x"
            ruby
            faded
            xs
            @click="emit('resetFilters')"
          />
        </template>
        <template v-if="hasActiveFolders">
          <div class="relative">
            <NextButton
              id="toggleConversationFilterButton"
              v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
              icon="i-lucide-pen-line"
              slate
              xs
              faded
              @click="emit('filtersModal')"
            />
            <div
              id="conversationFilterTeleportTarget"
              class="absolute z-50 mt-2"
              :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
            />
          </div>
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            icon="i-lucide-trash-2"
            ruby
            xs
            faded
            @click="emit('deleteFolders')"
          />
        </template>
        <div v-else class="relative">
          <NextButton
            id="toggleConversationFilterButton"
            v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
            icon="i-lucide-list-filter"
            slate
            xs
            faded
            @click="emit('filtersModal')"
          />
          <div
            id="conversationFilterTeleportTarget"
            class="absolute z-50 mt-2"
            :class="{ 'ltr:right-0 rtl:left-0': isOnExpandedLayout }"
          />
        </div>
        <ConversationBasicFilter
          v-if="!hasAppliedFiltersOrActiveFolders"
          :is-on-expanded-layout="isOnExpandedLayout"
          @change-filter="onBasicFilterChange"
        />
        <SwitchLayout
          :is-on-expanded-layout="isOnExpandedLayout"
          @toggle="toggleConversationLayout"
        />
      </div>
    </div>
    <div v-if="isSearchOpen" class="flex flex-col gap-2 px-3 pb-3">
      <NextInput
        :model-value="searchQuery"
        :placeholder="$t('CHAT_LIST.SEARCH.INPUT')"
        autofocus
        size="sm"
        type="search"
        class="group w-full [&>input]:ltr:!pl-8 [&>input]:rtl:!pr-8 [&>input]:ltr:!pr-8 [&>input]:rtl:!pl-8"
        @update:model-value="emit('update:searchQuery', $event)"
        @keydown.esc="toggleSearch"
      >
        <template #prefix>
          <Icon
            icon="i-lucide-search"
            class="absolute z-10 top-1/2 -translate-y-1/2 text-n-slate-11 group-focus-within:text-n-brand size-3.5 ltr:left-2.5 rtl:right-2.5"
          />
          <button
            v-if="hasSearchFilters"
            v-tooltip.left="$t('CHAT_LIST.SEARCH.CLEAR')"
            type="button"
            class="absolute z-10 top-1/2 -translate-y-1/2 text-n-slate-10 hover:text-n-slate-12 ltr:right-2.5 rtl:left-2.5"
            @click="clearSearch"
          >
            <Icon icon="i-lucide-x" class="size-3.5" />
          </button>
        </template>
      </NextInput>
      <div class="grid grid-cols-2 gap-2">
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ $t('CHAT_LIST.SEARCH.FROM_DATE') }}
          <input
            :value="searchSince"
            type="date"
            :max="searchUntil || undefined"
            class="!mb-0 !h-8 !w-full !rounded-lg !border-n-weak !bg-n-alpha-black2 !px-2 !text-xs text-n-slate-12"
            @input="emit('update:searchSince', $event.target.value)"
          />
        </label>
        <label class="flex flex-col gap-1 text-xs text-n-slate-11">
          {{ $t('CHAT_LIST.SEARCH.TO_DATE') }}
          <input
            :value="searchUntil"
            type="date"
            :min="searchSince || undefined"
            class="!mb-0 !h-8 !w-full !rounded-lg !border-n-weak !bg-n-alpha-black2 !px-2 !text-xs text-n-slate-12"
            @input="emit('update:searchUntil', $event.target.value)"
          />
        </label>
      </div>
    </div>
  </div>
</template>
